import { useMutation } from '@tanstack/react-query'
import { FormattedMessage, useIntl } from 'react-intl'

import { useErrorToast } from '@/hooks/useErrorToast'
import { formatDate, getBrowserTimeZone } from '@/lib/date'
import { downloadFile } from '@/lib/utils'
import { DownloadFile } from '@/shared/icons/solid'
import { Button, Details } from '@/shared/ui'

import { downloadInvoice } from '../../api'
import {
  SingleTransaction,
  WithdrawalSource,
} from '../../features/Transactions/types'
import { getTransactionAdditionalDetails } from '../../features/Transactions/utils'
import { StateCell } from '../TransactionsTable'

type Props = {
  transaction: SingleTransaction
}

export const TransactionStatusInformation = ({ transaction }: Props) => {
  const intl = useIntl()
  const notifyError = useErrorToast()

  const additionalDetails = getTransactionAdditionalDetails(transaction)

  const { mutateAsync, isPending } = useMutation({
    mutationFn: downloadInvoice,
  })

  const handleDownloadInvoice = async () => {
    if (
      additionalDetails?.withdrawalSource !==
      WithdrawalSource.PAYROLL_BULK_PAYMENT
    )
      return

    try {
      const response = await mutateAsync({ transactionId: transaction.id })

      downloadFile(
        response.data,
        intl.formatMessage({
          id: 'contractor.payment.invoice.label',
          defaultMessage: 'invoice.pdf',
        }),
      )
    } catch (error) {
      notifyError(error)
    }
  }

  return (
    <>
      <Details>
        <Details.Label>
          <FormattedMessage id="label.date" defaultMessage="Date" />
        </Details.Label>
        <Details.Value>
          {formatDate(transaction.createdOn, `d MMM. yyyy, HH:mm`)} (
          {getBrowserTimeZone()})
        </Details.Value>
      </Details>
      <Details>
        <Details.Label>
          <FormattedMessage id="label.status" defaultMessage="Status" />
        </Details.Label>
        <StateCell transaction={transaction} />
      </Details>
      {additionalDetails?.withdrawalSource ===
      WithdrawalSource.PAYROLL_BULK_PAYMENT ? (
        <Details>
          <Details.Label>
            <FormattedMessage id="label.invoice" defaultMessage="Invoice" />
          </Details.Label>
          <div className="flex items-center">
            <Button
              onClick={handleDownloadInvoice}
              disabled={isPending}
              loaderPosition="left"
              loading={isPending}
              leftIcon={<DownloadFile className="size-4" />}
              variant="link"
              size="inline"
            >
              <FormattedMessage
                id="action.download"
                defaultMessage="Download"
              />
            </Button>
          </div>
        </Details>
      ) : null}

      {additionalDetails?.taskOwnerFullName ? (
        <Details>
          <Details.Label>
            <FormattedMessage
              id="label.requestedBy"
              defaultMessage="Requested by"
            />
          </Details.Label>
          <Details.Value>{additionalDetails.taskOwnerFullName}</Details.Value>
        </Details>
      ) : null}
      {additionalDetails?.completedByFullName ? (
        <Details>
          <Details.Label>
            <FormattedMessage
              id="label.approvedBy"
              defaultMessage="Approved by"
            />
          </Details.Label>
          <Details.Value>{additionalDetails.completedByFullName}</Details.Value>
        </Details>
      ) : null}
    </>
  )
}
